/** Keyboard shortcut overlay — auto-shown for new users, togglable via button or ? key. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

// ─── Constants ──────────────────────────────────────────────────────────────

const SEEN_KEY = 'formency-shortcuts-seen';
const PINNED_KEY = 'formency-shortcuts-pinned';
/** Auto-show overlay for the first N steps of a new user's first session. */
const AUTO_SHOW_STEPS = 3;

// ─── Context ────────────────────────────────────────────────────────────────

interface ShortcutOverlayContextValue {
  showShortcuts: boolean;
  /** Whether shortcuts are permanently pinned on via the button or keyboard. */
  isPinned: boolean;
  /** Toggle persistent pin. Saved to localStorage. Used by both button and ? key. */
  togglePin: () => void;
}

const ShortcutOverlayContext = createContext<ShortcutOverlayContextValue | null>(null);

// ─── Helpers ────────────────────────────────────────────────────────────────

function getFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function setFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // ignore
  }
}

// ─── Provider ───────────────────────────────────────────────────────────────

interface ShortcutOverlayProviderProps {
  currentIndex: number;
  children: React.ReactNode;
}

export function ShortcutOverlayProvider({ currentIndex, children }: ShortcutOverlayProviderProps) {
  const [hasSeen, setHasSeen] = useState(() => getFlag(SEEN_KEY));
  const [isPinned, setIsPinned] = useState(() => getFlag(PINNED_KEY));

  // Auto-show for first N steps if user has never seen shortcuts
  const autoShow = !hasSeen && currentIndex < AUTO_SHOW_STEPS;

  const showShortcuts = isPinned || autoShow;

  // Derived state update during render (React-approved pattern):
  // once the user has navigated past the auto-show threshold, dismiss permanently.
  if (!hasSeen && currentIndex >= AUTO_SHOW_STEPS) {
    setHasSeen(true);
  }

  // Persist "seen" flag to localStorage as a side effect
  useEffect(() => {
    if (hasSeen) {
      setFlag(SEEN_KEY, true);
    }
  }, [hasSeen]);

  const togglePin = useCallback(() => {
    setIsPinned((prev) => {
      const next = !prev;
      setFlag(PINNED_KEY, next);
      if (next) {
        setHasSeen(true);
        setFlag(SEEN_KEY, true);
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ showShortcuts, isPinned, togglePin }),
    [showShortcuts, isPinned, togglePin]
  );

  return <ShortcutOverlayContext value={value}>{children}</ShortcutOverlayContext>;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useShortcutOverlay(): ShortcutOverlayContextValue {
  const ctx = useContext(ShortcutOverlayContext);
  if (!ctx) {
    throw new Error('useShortcutOverlay must be used within <ShortcutOverlayProvider>');
  }
  return ctx;
}

// ─── Kbd component ──────────────────────────────────────────────────────────

interface KbdProps {
  children: React.ReactNode;
  className?: string;
}

/** Pure presentational key-cap badge. */
export function Kbd({ children, className = '' }: KbdProps) {
  return (
    <kbd
      className={`inline-flex items-center justify-center ${className}`}
      style={{
        minWidth: '1.25rem',
        height: '1.25rem',
        padding: '0 0.3rem',
        borderRadius: 'var(--f-radius-sm)',
        border: '1px solid var(--f-border-strong)',
        backgroundColor: 'var(--f-bg-raised)',
        color: 'var(--f-fg-secondary)',
        fontFamily: 'var(--f-font-sans)',
        fontSize: '0.65rem',
        fontWeight: 600,
        lineHeight: 1,
        userSelect: 'none',
        pointerEvents: 'none',
        opacity: 0.6,
      }}
    >
      {children}
    </kbd>
  );
}

// ─── Animated Kbd badge ─────────────────────────────────────────────────────

interface AnimatedKbdProps {
  show: boolean;
  children: React.ReactNode;
  /** 'corner' = absolute top-right; 'inline' = in flow (default: corner) */
  position?: 'corner' | 'inline';
}

/**
 * Animated shortcut badge with fade in/out.
 * Handles AnimatePresence internally — just pass `show`.
 */
export function AnimatedKbd({ show, children, position = 'corner' }: AnimatedKbdProps) {
  if (position === 'inline') {
    return (
      <AnimatePresence>
        {show && (
          <motion.span
            initial={{ opacity: 0, maxWidth: 0, marginLeft: 0 }}
            animate={{ opacity: 1, maxWidth: 30, marginLeft: 6 }}
            exit={{ opacity: 0, maxWidth: 0, marginLeft: 0 }}
            transition={{ duration: 0.15 }}
            style={{ overflow: 'hidden', display: 'inline-flex', flexShrink: 0 }}
          >
            <Kbd>{children}</Kbd>
          </motion.span>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          style={{ position: 'absolute', top: 6, right: 6 }}
        >
          <Kbd>{children}</Kbd>
        </motion.span>
      )}
    </AnimatePresence>
  );
}

// ─── Global shortcut legend ─────────────────────────────────────────────────

const kbdStyle: React.CSSProperties = {
  minWidth: '1.25rem',
  height: '1.25rem',
  padding: '0 0.3rem',
  borderRadius: 'var(--f-radius-sm)',
  border: '1px solid var(--f-border-strong)',
  backgroundColor: 'var(--f-bg-raised)',
  color: 'var(--f-fg-secondary)',
  fontFamily: 'var(--f-font-sans)',
  fontSize: '0.65rem',
  fontWeight: 600,
  lineHeight: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.6,
};

export function ShortcutLegend() {
  const { showShortcuts } = useShortcutOverlay();

  return (
    <AnimatePresence initial={false}>
      {showShortcuts && (
        <motion.div
          initial={{ height: 0, opacity: 0, marginBottom: 0 }}
          animate={{ height: 'auto', opacity: 1, marginBottom: 12 }}
          exit={{ height: 0, opacity: 0, marginBottom: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          style={{ overflow: 'hidden' }}
        >
          <div
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1"
            style={{
              fontSize: 'var(--f-text-xs)',
              color: 'var(--f-fg-muted)',
              fontFamily: 'var(--f-font-sans)',
              padding: 'var(--f-space-2) 0',
            }}
          >
            <span className="inline-flex items-center gap-1">
              <kbd style={kbdStyle}>Enter</kbd>/<kbd style={kbdStyle}>Space</kbd>
              Check / Next
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd style={kbdStyle}>&larr;</kbd>
              <kbd style={kbdStyle}>&rarr;</kbd>
              Navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd style={kbdStyle}>Esc</kbd>
              Exit
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd style={kbdStyle}>?</kbd>
              Toggle shortcuts
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
